import type { ApiFn } from '@/api/factory/base/api';
import { useMakeQuery } from '@/api/use-make-query';
import { QUERY_KEYS } from '@/features/basket/api/query/index';
import useBasketId from '@/features/basket/hooks/use-basket-id';
import type { Basket } from '@/features/basket/types/basket';
import { useRemoveCookie } from '@/hooks/use-cookies';
import { Cookie } from '@/types/storage';
import { isAxiosError } from 'axios';
import { useEffect, useRef } from 'react';

export const useBasketQuery = (enabled: boolean = true) => {
  const apiFn: ApiFn<Basket> = (api) => api.get(`basket/${basketId}`);
  const basketId = useBasketId();
  const removeCookie = useRemoveCookie();
  const errorRef = useRef(false);

  const query = useMakeQuery({
    apiFn,
    queryKey: [QUERY_KEYS.BASKET, basketId],
    retry: false,
    enabled: !!basketId && enabled,
    throwOnError: (error: Error) => {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          // Mark that we've seen a 404 error, but don't update state here
          errorRef.current = true;
        }
      }
      return false;
    },
  });

  useEffect(() => {
    if (errorRef.current && query.error) {
      const error = query.error;
      if (isAxiosError(error) && error?.response?.status === 404) {
        removeCookie(Cookie.BASKET_ID);
        errorRef.current = false;
      }
    }
  }, [query.error, removeCookie]);

  return query;
};
